import { useState, useEffect } from 'react';
import { http } from '@/lib/http';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useUiStore } from '@/stores/ui';
import mermaid from 'mermaid';
import { Loader2 } from 'lucide-react';

export default function CircularDependencyPage() {
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
  const [graphs, setGraphs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHeaderTitle("Circular Dependencies");
    mermaid.initialize({ startOnLoad: false, theme: 'dark' });

    const fetchCycles = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await http<{ graphs: string[] }>('/api/indexing/circular-dependencies');
        setGraphs(response.graphs);
      } catch (e: any) {
        console.error("Failed to fetch circular dependencies", e);
        setError(e.message);
        toast.error("Failed to load circular dependencies.", { description: e.message });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCycles();
  }, [setHeaderTitle]);

  useEffect(() => {
    if (isLoading || graphs.length === 0) return;

    const renderGraphs = async () => {
      for (let i = 0; i < graphs.length; i++) {
        const container = document.getElementById(`mermaid-container-${i}`);
        if (container) {
          try {
            // Check if it's already rendered to avoid flickering on re-renders
            if (container.querySelector('svg')) continue;
            
            container.innerHTML = ""; // Clear previous
            const { svg } = await mermaid.render(`mermaid-svg-${i}`, graphs[i]);
            container.innerHTML = svg;
          } catch (e: any) {
            console.error("Mermaid rendering error:", e);
            container.innerHTML = `<p class="text-destructive">Error rendering graph: ${e.message}</p>`;
          }
        }
      }
    };
    
    // Using requestAnimationFrame to wait for the next paint, which is often enough
    requestAnimationFrame(renderGraphs);
  }, [graphs, isLoading]);

  return (
    <div className="container mx-auto p-4 lg:p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Circular Dependency Analysis</h1>
        <p className="text-muted-foreground">
          Found {graphs.length} circular dependency group(s) in the project.
        </p>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="ml-2">Analyzing import graph...</p>
        </div>
      )}

      {error && (
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Could not analyze dependencies: {error}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && graphs.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>No Cycles Found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Congratulations! Your project has no circular import dependencies.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && graphs.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {graphs.map((_, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle>Cycle {index + 1}</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  id={`mermaid-container-${index}`}
                  className="flex items-center justify-center min-h-[200px] overflow-auto"
                >
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}